import axios, { AxiosResponse } from "axios";
import type { Store } from "@reduxjs/toolkit";
import { refreshAuthToken } from "../features/auth/api/authApi";
import {
  setToken,
  setRefreshToken,
  logout,
} from "../features/auth/slices/authSlice";
import { redirectToLogin } from "../services/redirect";
import { toast } from "react-toastify";
import { extractErrorMessage } from "../utils/extractErrorMessage";

const NO_RETRY_HEADER = "x-no-retry";
let errorNotificationInterceptorSet = false;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

/**
 * Setup interceptors after the store is created
 * to avoid circular imports.
 */
export function initializeAxiosInterceptors(store: Store) {
  // Request Interceptor
  axiosInstance.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const token = state.auth.token;

      if (token && !config.url?.includes("/token/refresh")) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      if (process.env.REACT_APP_API_DEBUG === "true") {
        const separator = config.url?.includes("?") ? "&" : "?";
        config.url += `${separator}XDEBUG_SESSION_START=PHPSTORM`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response Interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest.headers[NO_RETRY_HEADER] &&
        !originalRequest.url.includes("/token/refresh") // Added this check to prevent infinite loop
      ) {
        // Mark the request so we don't retry infinitely
        originalRequest.headers[NO_RETRY_HEADER] = "true";
        const state = store.getState();
        const refreshTokenVal = state.auth.refreshToken;

        if (refreshTokenVal) {
          try {
            const refreshAuthTokenResponse =
              await refreshAuthToken(refreshTokenVal);

            store.dispatch(setToken(refreshAuthTokenResponse.token));
            store.dispatch(
              setRefreshToken(refreshAuthTokenResponse.refresh_token),
            );

            originalRequest.headers["Authorization"] =
              `Bearer ${refreshAuthTokenResponse.token}`;
            delete originalRequest.headers[NO_RETRY_HEADER];

            return axiosInstance(originalRequest);
          } catch (refreshError) {
            store.dispatch(logout());
            redirectToLogin();
            return Promise.reject(refreshError);
          }
        } else {
          store.dispatch(logout());
          redirectToLogin();
        }
      } else if (error.response && error.response.status === 403) {
        window.location.assign("/403");
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );
}

/**
 * Add a separate interceptor for error notifications,
 * but make sure it's only added once.
 */
export function addErrorNotificationInterceptor() {
  if (errorNotificationInterceptorSet) {
    return;
  }

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response) {
        try {
          const message = await extractErrorMessage(
            error.response as AxiosResponse,
          );
          toast.error(message);
        } catch (e) {
          console.error("Failed to extract error message", e);
          toast.error("An error occurred.");
        }
      } else {
        toast.error("Network error. No response from server.");
      }
      return Promise.reject(error);
    },
  );

  errorNotificationInterceptorSet = true;
}

export default axiosInstance;
